import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, SkipBack, SkipForward, Camera } from 'lucide-react';

interface PathActivityVideoProps {
  videoUrl?: string;
  timestamp?: string;
  activityType?: string;
}

const PathActivityVideo: React.FC<PathActivityVideoProps> = ({
  videoUrl = '/videos/emma_martinez_activity.mp4',
  timestamp = new Date().toLocaleTimeString(),
  activityType = 'Walking to Kitchen'
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.addEventListener('loadedmetadata', () => {
        setDuration(videoRef.current?.duration || 0);
        setIsLoaded(true);
      });

      videoRef.current.addEventListener('error', (e) => {
        console.error('Error loading video:', e);
        setIsLoaded(false);
      });
    }

    return () => {
      if (videoRef.current) {
        videoRef.current.removeEventListener('loadedmetadata', () => {});
        videoRef.current.removeEventListener('error', () => {});
      }
    };
  }, [videoRef.current]);

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const skipForward = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.min(videoRef.current.duration, currentTime + 10);
    }
  };

  const skipBackward = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.max(0, currentTime - 10);
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
      <div className="relative bg-gray-900">
        {!isLoaded && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-white text-center">
              <Camera size={48} className="mx-auto mb-2 animate-pulse" />
              <p>Loading activity visualization...</p>
            </div>
          </div>
        )}
        <video
          ref={videoRef}
          className="w-full h-auto"
          onTimeUpdate={handleTimeUpdate}
          playsInline
          muted
          loop
          autoPlay
        >
          <source src={videoUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        
        <div className="absolute top-4 left-4 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
          {timestamp}
        </div>
        
        <div className="absolute top-4 right-4 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
          {activityType}
        </div>
      </div>

      <div className="p-4 bg-gray-50">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-500">{formatTime(currentTime)}</span>
          <div className="flex items-center space-x-4">
            <button
              onClick={skipBackward}
              className="p-2 hover:bg-gray-200 rounded-full transition-colors"
              disabled={!isLoaded}
            >
              <SkipBack size={20} className={!isLoaded ? 'opacity-50' : ''} />
            </button>
            <button
              onClick={handlePlayPause}
              className="p-2 hover:bg-gray-200 rounded-full transition-colors"
              disabled={!isLoaded}
            >
              {isPlaying ? 
                <Pause size={20} className={!isLoaded ? 'opacity-50' : ''} /> : 
                <Play size={20} className={!isLoaded ? 'opacity-50' : ''} />
              }
            </button>
            <button
              onClick={skipForward}
              className="p-2 hover:bg-gray-200 rounded-full transition-colors"
              disabled={!isLoaded}
            >
              <SkipForward size={20} className={!isLoaded ? 'opacity-50' : ''} />
            </button>
          </div>
          <span className="text-sm text-gray-500">{formatTime(duration)}</span>
        </div>

        <input
          type="range"
          min="0"
          max={duration}
          value={currentTime}
          onChange={handleSeek}
          disabled={!isLoaded}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer disabled:opacity-50"
        />
      </div>
    </div>
  );
};

export default PathActivityVideo; 